import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Target, Ticket, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { API_URL as API } from '../config/api';

const ProgressBar = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    totalRaised: 15750,
    goal: 500000,
    ticketsSold: 315,
    participants: 287
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/campaign/stats`);
        if (response.data) {
          setStats(prev => ({
            ...prev,
            ...response.data
          }));
        }
      } catch (error) {
        console.log('Using default stats');
      }
    };
    fetchStats();
  }, []);

  const percentage = Math.min((stats.totalRaised / stats.goal) * 100, 100);

  const formatNumber = (num) => {
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'fr-FR').format(num);
  };

  return (
    <section className="py-12 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {/* Progress Bar */}
          <div className="bg-gray-100 rounded-2xl p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Target className="w-6 h-6 text-primary" />
                <span className="font-semibold text-gray-700">{t('progress.progress')}</span>
              </div>
              <span className="text-2xl font-bold text-primary">{percentage.toFixed(1)}%</span>
            </div>
            
            {/* Bar */}
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${percentage}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-secondary to-accent rounded-full"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-gray-700">
                  ${formatNumber(stats.totalRaised)} / ${formatNumber(stats.goal)}
                </span>
              </div>
            </div>

            <div className="flex justify-between mt-4 text-sm">
              <div>
                <span className="text-gray-500">{t('progress.raised')}: </span>
                <span className="font-bold text-primary">${formatNumber(stats.totalRaised)}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('progress.goal')}: </span>
                <span className="font-bold text-gray-700">${formatNumber(stats.goal)}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-primary/5 rounded-2xl p-6 text-center"
            >
              <TrendingUp className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-3xl font-bold text-primary">${formatNumber(stats.totalRaised)}</p>
              <p className="text-sm text-gray-600">{t('progress.raised')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-secondary/10 rounded-2xl p-6 text-center"
            >
              <Target className="w-8 h-8 text-secondary mx-auto mb-2" />
              <p className="text-3xl font-bold text-secondary">${formatNumber(stats.goal)}</p>
              <p className="text-sm text-gray-600">{t('progress.goal')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-accent/10 rounded-2xl p-6 text-center"
            >
              <Ticket className="w-8 h-8 text-accent mx-auto mb-2" />
              <p className="text-3xl font-bold text-accent">{formatNumber(stats.ticketsSold)}</p>
              <p className="text-sm text-gray-600">{t('progress.tickets')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-primary/5 rounded-2xl p-6 text-center"
            >
              <Users className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-3xl font-bold text-primary">{formatNumber(stats.participants)}</p>
              <p className="text-sm text-gray-600">{t('progress.participants')}</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProgressBar;